import React from "react";
import SEO from "../components/SEO";
import { CheckCircle } from "lucide-react";

const CaseStudiesScreen: React.FC = () => {
  const caseStudies = [
    {
      id: "govt-cctv",
      title: "SecureHorizon: Government Facility Surveillance Revamp",
      client: "Jigawa State Government Agency",
      location: "Dutse, Nigeria",
      challenge:
        "A 10-acre administrative complex faced persistent security breaches due to blind spots in their analog CCTV system. Night surveillance was non-existent due to poor low-light cameras.",
      solution:
        "We deployed a comprehensive IP-based surveillance network comprising 45 high-definition AI-powered cameras with night vision. The system included a central command center with video wall monitoring and automated motion detection alerts.",
      result:
        "Security incidents reduced by 40% within the first 3 months. The facility now achieves 100% visual coverage with real-time remote monitoring capabilities.",
      tech: [
        "4K IP Cameras",
        "Fiber Optic Backbone",
        "AI Video Analytics",
        "Solar Power Backup",
      ],
      image: "/services/cctv.jpg", // Placeholder or reuse existing asset
    },
    {
      id: "ecommerce-scale",
      title: "MarketFlow: E-Commerce Platform Scaling",
      client: "Leading Retail Chain",
      location: "Kano, Nigeria",
      challenge:
        "A rapidly growing retail business struggled with their legacy website crashing during peak sales periods. Content management was rigid, and mobile performance was poor.",
      solution:
        "InnovaTech re-architected the platform using a MERN stack (MongoDB, Express, React, Node.js) hosted on scalable cloud infrastructure. We implemented Redis caching and a PWA (Progressive Web App) architecture for offline reliability.",
      result:
        "Achieved 99.99% uptime during Black Friday sales. Mobile conversions increased by 150%, and total online revenue grew by 300% in the first quarter.",
      tech: ["React.js", "Node.js", "AWS Cloud", "Redis"],
      image: "/services/web.jpg",
    },
    {
      id: "uni-network",
      title: "CampusConnect: High-Density University Wi-Fi",
      client: "Private University",
      location: "Northern Nigeria",
      challenge:
        "Over 5,000 students and staff experienced slow, unreliable internet access. Dead zones were common in dormitories and lecture halls, affecting digital learning initiatives.",
      solution:
        "We designed and installed a campus-wide Gigabit network using fiber optic backhaul and high-density Ruckus Wireless Access Points. We configured bandwidth management policies to prioritize educational traffic.",
      result:
        "Seamless connectivity across 95% of the campus. Student satisfaction with IT services rose from 45% to 92%. Online learning participation increased significantly.",
      tech: [
        "Fiber Optics",
        "Ruckus APs",
        "Network Security",
        "Bandwidth Management",
      ],
      image: "/services/networking.jpg",
    },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Case Studies - InnovaTech Consultancy",
    description:
      "Explore our success stories implementing CCTV, Web Development, and Network Solutions across Nigeria.",
    mainEntity: caseStudies.map((study) => ({
      "@type": "Article",
      headline: study.title,
      alternativeHeadline: `Case Study: ${study.title}`,
      description: study.challenge,
      author: {
        "@type": "Organization",
        name: "InnovaTech Consultancy Limited",
      },
      contentLocation: {
        "@type": "Place",
        name: study.location,
      },
    })),
  };

  return (
    <div className="animate-fade-in bg-canvas dark:bg-[#0C1B2E] min-h-screen">
      <SEO
        title="Case Studies | InnovaTech"
        description="Read how InnovaTech solves complex tech challenges in Nigeria. Case studies on CCTV installation, High-scale Web Apps, and University Networking."
        canonical="/case-studies"
      >
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </SEO>

      <header className="relative py-24 px-4 bg-surface dark:bg-[#151f2b] border-b border-border-subtle dark:border-white/10">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="mb-6 inline-flex items-center justify-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1">
            <span className="text-xs font-bold text-primary dark:text-red-400 uppercase tracking-widest">
              Proven Results
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-text-main dark:text-white mb-6 tracking-tight">
            We Don't Just Write Code.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-text-main dark:to-white">
              We Solve Problems.
            </span>
          </h1>
          <p className="text-lg text-text-sub dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Deep dive into our engineering process. See how we transform
            challenge into opportunity for government agencies, educational
            institutions, and enterprises.
          </p>
        </div>
      </header>

      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl flex flex-col gap-24">
          {caseStudies.map((study, index) => (
            <div
              key={study.id}
              className="flex flex-col md:flex-row gap-8 md:gap-16 items-start group"
            >
              {/* Content Side */}
              <div className={`flex-1 ${index % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-text-sub dark:text-gray-400 border border-border-subtle dark:border-white/10 px-2 py-1 rounded bg-white dark:bg-white/5">
                    {study.client}
                  </span>
                  <span className="block h-1 w-1 rounded-full bg-primary"></span>
                  <span className="text-xs font-medium text-text-sub dark:text-gray-500">
                    {study.location}
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-text-main dark:text-white mb-6 group-hover:text-primary transition-colors">
                  {study.title}
                </h2>

                <div className="space-y-8 relative">
                  <div className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-border-subtle dark:bg-white/5"></div>

                  <div className="relative pl-8">
                    <div className="absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 border-primary bg-canvas dark:bg-[#0C1B2E]"></div>
                    <h3 className="text-sm font-bold text-text-main dark:text-white uppercase tracking-wide mb-2">
                      The Challenge
                    </h3>
                    <p className="text-text-sub dark:text-gray-400 leading-relaxed text-sm">
                      {study.challenge}
                    </p>
                  </div>

                  <div className="relative pl-8">
                    <div className="absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 border-primary bg-primary"></div>
                    <h3 className="text-sm font-bold text-text-main dark:text-white uppercase tracking-wide mb-2">
                      Our Solution
                    </h3>
                    <p className="text-text-sub dark:text-gray-400 leading-relaxed text-sm">
                      {study.solution}
                    </p>
                  </div>

                  <div className="relative pl-8">
                    <div className="absolute left-0 top-1.5 h-4 w-4 rounded-full bg-green-500"></div>
                    <h3 className="text-sm font-bold text-green-600 dark:text-green-400 uppercase tracking-wide mb-2">
                      The Result
                    </h3>
                    <p className="text-text-main dark:text-white font-medium leading-relaxed">
                      {study.result}
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-2">
                  {study.tech.map((tech) => (
                    <span
                      key={tech}
                      className="text-[10px] font-bold uppercase tracking-widest text-text-sub dark:text-gray-400 bg-surface dark:bg-white/5 px-3 py-1.5 rounded-full border border-border-subtle dark:border-white/10"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Visual Side (Simulated Card) */}
              <div
                className={`flex-1 w-full ${index % 2 === 1 ? "md:order-1" : ""}`}
              >
                <div className="aspect-[4/3] bg-surface dark:bg-[#151f2b] rounded-2xl border border-border-subtle dark:border-white/10 p-2 shadow-2xl skew-y-1 group-hover:skew-y-0 transition-transform duration-700 ease-out-expo">
                  <div className="h-full w-full bg-slate-200 dark:bg-white/5 rounded-xl overflow-hidden relative">
                    <div className="absolute inset-0 flex items-center justify-center text-text-sub dark:text-white/20 font-black text-6xl uppercase opacity-20 rotate-12">
                      {study.id.split("-")[0]}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                      <div className="flex items-center gap-2 text-white/80 text-xs font-mono">
                        <CheckCircle className="w-4 h-4" />
                        Verified Success Story
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesScreen;
